/**
 * The LedgerSync connection status vocabulary (`ledgersync_connections.status`).
 *
 * Shared because the Deno webhook and the Node org-level reconcile both copy the
 * vendor's live status onto the row, and both must refuse a value the CHECK
 * constraint `ledgersync_connections_status_check` would reject — a new vendor
 * status must not fail the write. The edge functions import this file through
 * the `@dcp/shared/` import map, so its only dependency is the shared dayjs
 * entry point, which resolves in Deno through the same map.
 */

import dayjs from '../utils/dayjs.ts'

/** Every value `ledgersync_connections.status` may hold (mirrors the CHECK). */
export const LEDGERSYNC_CONNECTION_STATUSES = [
  'initiated',
  'requires_action',
  'active',
  'failed',
  'disconnected',
] as const

/** One value of {@link LEDGERSYNC_CONNECTION_STATUSES}. */
export type LedgerSyncConnectionStatus = (typeof LEDGERSYNC_CONNECTION_STATUSES)[number]

/**
 * Whether a vendor-reported status can be stored as is.
 *
 * @param value - status string as read from the vendor
 * @returns true when it is one of {@link LEDGERSYNC_CONNECTION_STATUSES}
 */
export function isLedgerSyncConnectionStatus(value: string): value is LedgerSyncConnectionStatus {
  return (LEDGERSYNC_CONNECTION_STATUSES as readonly string[]).includes(value)
}

/**
 * Whether a connection id is CANONICAL (`con_<SOURCE>_<id>`) rather than the
 * `con_<uuid>` placeholder an initiate returns. The uppercase anchor separates
 * them: a placeholder's UUID is lowercase hex. Every endpoint except the widget
 * step rejects a placeholder with 400, so check a stored id before using it.
 *
 * @param connectionId - A v3 connection id, or null/undefined when unresolved
 * @returns True only for a canonical id
 */
export function isCanonicalConnectionId(connectionId: string | null | undefined): boolean {
  return !!connectionId && /^con_[A-Z]/.test(connectionId)
}

/** The fields of a vendor connection read that decide what we store. */
export interface LiveConnectionState {
  /** Null when the vendor sent no status — never stored, see {@link liveConnectionPatch}. */
  status: string | null
  lastError: string | null
  lastRefreshedAt: string | null
}

/** The `ledgersync_connections` columns that mirror the vendor's live connection. */
export interface LiveConnectionPatch {
  status: LedgerSyncConnectionStatus
  last_error: string | null
  /** Present only when the vendor returned one; a stored stamp is never cleared. */
  last_refreshed_at?: string
}

/**
 * A status we already know about, with its reason: the stored row, or the
 * delivered event.
 */
export interface KnownConnectionState {
  status: string
  lastError: string | null
}

/**
 * Error text with blanks normalized away: null, `""` and whitespace all mean
 * "no reason given".
 *
 * @param text - error text from the vendor, an event or a stored row
 * @returns the text, or null when there is none
 */
export function nonBlankError(text: string | null | undefined): string | null {
  return text != null && text.trim() !== '' ? text : null
}

/**
 * The reason to keep for `status` when the vendor reports it without one: the
 * first entry of `known` with the same status and a non-blank reason.
 *
 * @param status - the status about to be stored
 * @param known - candidates in priority order (the webhook passes the event,
 *        then the stored row; the org reconcile passes the stored row)
 * @returns that reason, or null
 */
export function keptReason(status: string, known: readonly KnownConnectionState[]): string | null {
  for (const k of known) {
    const reason = nonBlankError(k.lastError)
    if (k.status === status && reason !== null) return reason
  }
  return null
}

/**
 * Whether a live `initiated` must be refused over the stored status.
 *
 * `initiated` is only ever written by the connect flow, for a connect that has
 * not reached the widget yet. Read from the vendor over any other stored status
 * (e.g. while a Reconnect re-runs on a linked connection) it would turn a
 * connection into a neutral "Connecting…" that the UI does not flag and the
 * scheduled sync skips — frozen figures with no signal.
 *
 * @param liveStatus - the status the vendor reports
 * @param storedStatus - the status on our row
 * @returns true when the write must not happen
 */
export function refusesLiveInitiated(liveStatus: string | null, storedStatus: string): boolean {
  return liveStatus === 'initiated' && storedStatus !== 'initiated'
}

/**
 * The row write that makes a stored connection match the vendor's live one —
 * the one rule the webhook, the connect poll and the org-level reconcile share.
 *
 * `status` is the vendor's. The patch is null — the caller leaves the row as it
 * is and logs why — when the live status is missing or outside our CHECK set
 * (never store a guess; a missing status must never read as `initiated`), or
 * when it is `initiated` over a stored status that is not
 * ({@link refusesLiveInitiated}: `initiated` is only ever written by the
 * connect flow).
 *
 * `last_error` is the vendor's text, except: an `active` connection carries
 * none; and when the vendor gives no text, the reason of the first `known`
 * entry with the SAME status is kept ({@link keptReason}) — the read agrees on
 * the state and simply did not repeat why. For the webhook that is the
 * delivery's reason, else the reason stored on the row (e.g. an auth-expiry
 * message from the transaction reader); for the reconcile and the poll, the
 * row's.
 *
 * @param live - status, error and refresh stamp from the vendor
 * @param storedStatus - the status on our row
 * @param known - candidates whose reason may be kept, in priority order
 * @returns the patch, or null when nothing may be written
 */
export function liveConnectionPatch(
  live: LiveConnectionState,
  storedStatus: string,
  known: readonly KnownConnectionState[],
): LiveConnectionPatch | null {
  if (live.status === null || !isLedgerSyncConnectionStatus(live.status)) return null
  if (refusesLiveInitiated(live.status, storedStatus)) return null
  const lastError =
    live.status === 'active' ? null : (nonBlankError(live.lastError) ?? keptReason(live.status, known))
  const patch: LiveConnectionPatch = { status: live.status, last_error: lastError }
  if (live.lastRefreshedAt) patch.last_refreshed_at = live.lastRefreshedAt
  return patch
}

/** The stored row fields a patch is compared with. */
export interface StoredConnectionState {
  status: string
  last_error: string | null
  last_refreshed_at: string | null
}

/**
 * Whether the stored row already says what the patch would write, so the
 * UPDATE can be skipped. Compared: status, last_error, and last_refreshed_at
 * when the patch carries one — as instants, because Postgres answers
 * `…+00:00` and the vendor `…Z` for the same moment.
 *
 * @param stored - the row as read
 * @param patch - the write about to happen
 * @returns true when writing would change nothing
 */
export function patchMatchesStored(stored: StoredConnectionState, patch: LiveConnectionPatch): boolean {
  if (stored.status !== patch.status) return false
  if (stored.last_error !== patch.last_error) return false
  if (patch.last_refreshed_at === undefined) return true
  return stored.last_refreshed_at !== null && dayjs(stored.last_refreshed_at).isSame(patch.last_refreshed_at)
}

/**
 * Whether a CONDITIONAL write (compare-and-set on `updated_at` or `status`)
 * actually applied, judged from the row read back after it.
 *
 * Such writes run `return=minimal` — PostgREST answers 42703 when an UPDATE
 * that filters on a column it sets is asked for a body or a count — so the
 * outcome is read back separately. The row says what the patch says → applied
 * (or a concurrent writer stored the same, which is the same outcome); anything
 * else, or no row → a newer writer won and our write was skipped.
 *
 * @param after - the row read back after the write; null when it is gone
 * @param patch - what was written
 * @returns true when the stored row now says what the patch says
 */
export function conditionalWriteApplied(after: StoredConnectionState | null, patch: LiveConnectionPatch): boolean {
  return after !== null && patchMatchesStored(after, patch)
}
